import { Children, Fragment, isValidElement, type ReactNode } from "react";

/**
 * Flatten a component's `children` into a keyed, gap-free array: recursively
 * unwraps top-level React `<Fragment>`s and drops `null`/`false`/`undefined`.
 *
 * Consumers that map over children BY INDEX need every real child to occupy its
 * own position — e.g. the design-system `BetaDataTable`, which assigns
 * per-column sticky props by child index, or `TruncatedRow`, which wraps each
 * child. `React.Children.*` does NOT descend into Fragments and counts falsy
 * children, so a `<Fragment>`-grouped cell block or a `{cond && <Cell/>}` that
 * renders `false` would otherwise shift or collapse indices. Unwrapping
 * fragments first and calling `Children.toArray` once at the end yields a flat
 * array with stable, unique keys.
 */
export function flattenChildren(children: ReactNode): ReactNode[] {
  const flat: ReactNode[] = [];
  const walk = (nodes: ReactNode): void => {
    Children.forEach(nodes, (child) => {
      if (
        isValidElement<{ children?: ReactNode }>(child) &&
        child.type === Fragment
      ) {
        walk(child.props.children);
      } else {
        flat.push(child);
      }
    });
  };
  walk(children);
  // Single toArray at the top level drops falsy children and assigns unique
  // keys (nested per-level toArray would risk duplicate keys once combined).
  return Children.toArray(flat);
}
